import React, { ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "../PIE.css";
import { ProfileOverviewState } from "../../../redux/reducers/getProfile/getProfileOverviewReducer";
import { SaveDataState } from "../../../redux/reducers/getSaveData/saveDataReducer";
import { getProfileAttributes, putProfileAttributes, resetGetProfileAttributes } from "../../../redux/actions/profile/profile";
import NumberOfSitesData from "../../../types/numberOfSites/numberOfSitesData";
import updateProperties from "../../../utils/DataModifier/DataModifier";
import { SaveResultState } from "../../../redux/reducers/getSaveData/saveResultReducer";
import { ProfileAttributesState } from "../../../redux/reducers/getProfile/profileAttributes/getAttributesReducer";
import { getFormErrorByName, useFormErrorContext } from "../../../utils/context/FormErrorContext";
import ErrorMessages from "../../../components/common/ErrorMessages/ErrorMessages";
import { useNavigation } from "../../../utils/context/NavigationContext";
import { useTranslation } from "react-i18next";
import { commaSeparate } from "../../../utils/formatter";
import NumberOfSitesTab from "./NumberOfSitesTab";
import { accordionDisplay, getCurrentCampaignYear, getLastYearSubmittedCampaign, shouldDisplayPreviousTwoYearAttributes, shouldDisplayPreviousYearAttributes } from "../../../utils/previousIncomeAttributes";
import { GET_PREVIOUS_PROFILE_ATTRIBUTES } from "../../../redux/actionTypes/actionsTypes";
import PreviousCampaignIncomeAttributes from "../../../components/common/PreviousCampaignIncomeAttributes/PreviousCampaignIncomeAttributes";
import { selectMenu, selectPreviousProfileAttributes, selectProfileGetAttributes, selectProfileGetOverview, selectSaveData, selectSaveResult } from "../../../redux/selectors/exportSelectorPaths";
import AccordionItem, { DefaultAccordionProviderOptions } from "../../../components/common/AccordionItem/AccordionItem";
import { ControlledAccordion, useAccordionProvider } from "@szhsin/react-accordion";

export interface NumberOfSitesProps {
    readonly?: boolean;
    profileAttributes?: Record<string, number | null>;
}

function NumberOfSites(props: NumberOfSitesProps): ReactElement {
    const menuState = useSelector(selectMenu);
    const saveData: SaveDataState = useSelector(selectSaveData)
    const saveResultState: SaveResultState = useSelector(selectSaveResult)
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview)
    const profileAttributes: ProfileAttributesState = useSelector(selectProfileGetAttributes)
    const previousProfileAttributes: ProfileAttributesState = useSelector(selectPreviousProfileAttributes)

    const profileType = profileOverview.profile.profile_type_code;
    const dispatch = useDispatch()
    const numberOfSiteKeys = ["occupiedLot", "vacantLot", "totalSiteCount", "transientLot", "campgroundSite"]
    const { errors, clearErrors, removeError } = useFormErrorContext();
    const initialNumberOfSitesData: NumberOfSitesData = {
        "occupiedLot": null,
        "vacantLot": null,
        "totalSiteCount": null,
        "transientLot": null,
        "campgroundSite": null,
    }
    const [numberSitesData, setNumberSitesData] = useState<NumberOfSitesData>(initialNumberOfSitesData as NumberOfSitesData)
    const { direction } = useNavigation();
    const { t } = useTranslation(['number_of_sites', 'navigation']);
    const providerValue = useAccordionProvider(DefaultAccordionProviderOptions)
    const isInternal = (sessionStorage.getItem("isInternalUser") as string) === "true"

    useEffect(() => {
        if (!props.profileAttributes) {
            dispatch(resetGetProfileAttributes())
            dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, Number(sessionStorage.getItem("currentSnapshotId") as string)))

            const campaign = getLastYearSubmittedCampaign(profileOverview.profile)

            if (campaign) {
                dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, campaign.currentSnapshotId, GET_PREVIOUS_PROFILE_ATTRIBUTES))
            }
        } else {
            const initData = { ...initialNumberOfSitesData }
            updateProperties(initData, props.profileAttributes)
            setNumberSitesData(initData)
        }
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (!props.profileAttributes && !profileAttributes.loading && profileAttributes.data) {
            const initData = { ...initialNumberOfSitesData }
            updateProperties(initData, profileAttributes.data as Record<string, number | null>)
            setNumberSitesData(initData)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileAttributes.data, profileAttributes.loading])

    const handleNumberOfSitesChange = (newAmt: number | undefined | null, name: string, _oldAmt?: number | undefined | null) => {
        // const currentTarget = e.currentTarget
        // const value = newAmt
        removeError(getFormErrorByName(errors, name)?.primaryKey as string)
        const na = (newAmt === undefined || newAmt === null) ? null : newAmt

        setNumberSitesData({ ...numberSitesData, [name]: na })
    }

    useEffect(() => {
        if (saveData.saving && !props.readonly) {
            clearErrors()
            const dataOriginal = profileAttributes
            const data = { ...dataOriginal.data, ...numberSitesData } as Record<string, unknown>
            const triggerSpectral = isInternal || direction !== "backward"

            dispatch(putProfileAttributes(profileOverview.profile.profile_type_code, Number(sessionStorage.getItem("currentSnapshotId") as string), {
                "profile_attributes": data,
                "trigger_spectral": triggerSpectral,
                "spectral_section": "num_sites",
                "is_internal": isInternal
            }))
        }
    }, [dispatch, clearErrors, direction, numberSitesData, profileAttributes, profileType, profileOverview.profile.profile_type_code, saveData.saving, props.readonly, isInternal])

    useEffect(() => {
        if (saveResultState.status === 200) {
            dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, Number(sessionStorage.getItem("currentSnapshotId") as string)))
        }
    }, [dispatch, profileOverview.profile.profile_type_code, saveResultState.status])

    return (
        <>
            {props.readonly ?
                <table className='summary-table occupancy-statistics-table summary-table-headerless'>
                    <tbody>
                        {numberOfSiteKeys.map((key, i) => {
                            return <tr key={`occupancy-statistics-row-${i}`}>
                                <td>
                                    {t(key)}
                                </td>
                                <td >
                                    {commaSeparate(numberSitesData[key as keyof NumberOfSitesData])}
                                </td>
                            </tr>
                        })}
                    </tbody>
                </table>
                :
                <div className="full-width">
                    <div className="content-container">
                        <ErrorMessages errors={errors} />
                        {
                            (!profileAttributes.loading && !previousProfileAttributes.loading) &&
                            <div className="accordion-root">
                                <div className={accordionDisplay(isInternal, "accordion")}>
                                    <ControlledAccordion providerValue={providerValue}>
                                        <AccordionItem header={t(menuState.activeItem.name, { ns: 'navigation' })}>
                                            <NumberOfSitesTab keys={numberOfSiteKeys}
                                                valueChangeHandler={handleNumberOfSitesChange}
                                                data={numberSitesData} />
                                        </AccordionItem>
                                    </ControlledAccordion>
                                </div>
                                {shouldDisplayPreviousYearAttributes() ? <PreviousCampaignIncomeAttributes keys={numberOfSiteKeys} tabComponent='numberOfSites' forcedTab year={String(getCurrentCampaignYear() - 1)} /> : <></>}
                                {isInternal && shouldDisplayPreviousTwoYearAttributes() ? <PreviousCampaignIncomeAttributes keys={numberOfSiteKeys} tabComponent='numberOfSites' forcedTab year={String(getCurrentCampaignYear() - 2)} /> : <></>}
                            </div>
                        }
                    </div>
                </div>
            }
        </>
    )
}

export default NumberOfSites
